import { GetTripInput, TripOutput } from '@allocab/allocab-passenger-shared-types';
import { useIsMutating, useQuery } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import jwtDecode from 'jwt-decode';

import { mutationKeys } from '../services/datastore/mutationKeys';
import { queryKeys } from '../services/datastore/queryKeys';
import { silentQueryErrorHandler } from '../services/datastore/react-query';
import { passengerApi } from '../services/network/Api';
import { AnonymousTokenData } from '../utils/common';

type Input = GetTripInput & { jwtToken: string };

export const fetchAnonymousTrip = ({ jwtToken, ...input }: Input) =>
  passengerApi.get<GetTripInput, TripOutput>('/anonymous/v1/trip', input, {
    headers: { Authorization: `Bearer ${jwtToken}` },
  });

export const useFetchAnonymousTrip = (
  jwtToken: string,
  options: Omit<UseQueryOptions<TripOutput, unknown, TripOutput, (string | undefined)[]>, 'queryKey' | 'queryFn'> = {},
) => {
  const tokenData = jwtDecode(jwtToken) as AnonymousTokenData;

  const isCancelling = useIsMutating(mutationKeys.askAnonymousCancellation(jwtToken));
  const isConfirmingCancellation = useIsMutating(mutationKeys.confirmAnonymousCancellation(jwtToken));
  const isNoting = useIsMutating(mutationKeys.createAnonymousNote(jwtToken));
  const isUpdating = useIsMutating(mutationKeys.updateAnonymousTrip(jwtToken));
  const isConfirmingAnonymousUpdate = useIsMutating(mutationKeys.anonymousConfirmUpdateTrip(jwtToken));
  const isUpdatingTransportNumber = useIsMutating(mutationKeys.updateAnonymousTripTransportNumber(jwtToken));
  const tripQuery = useQuery(
    queryKeys.fetchAnonymousTrip(jwtToken),
    () => fetchAnonymousTrip({ key: tokenData.fareEncodedKey, jwtToken }),
    {
      ...options,
      enabled:
        !(options.enabled === false) &&
        !isCancelling &&
        !isNoting &&
        !isConfirmingCancellation &&
        !isUpdating &&
        !isUpdatingTransportNumber &&
        !isConfirmingAnonymousUpdate,
      onError: silentQueryErrorHandler,
    },
  );

  return tripQuery;
};
